import React, { useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Text } from 'rebass';
import Button from '@/components/Button/Button';
import Modal, { ModalBody, ModalFooter } from '@/components/Modal';
import { GridTable } from '@/components/Table/GridTable';
import useNotify from '@/hooks/useNotify';
import { registerPasskey } from '@/services/auth';
import ConfirmPasswordModal from './ConfirmPasswordModal';
import passkeyColumns from './Passkeys.columns';
import { DELETE_PASSKEY, SECURITY_SETTINGS, Passkey } from './Security.queries';

type PasskeysModalProps = {
  passkeys: Passkey[];
  hasPassword: boolean;
  onAdded: () => Promise<unknown>;
  onClose: () => void;
};

const PasskeysModal: React.FC<PasskeysModalProps> = ({
  passkeys,
  hasPassword,
  onAdded,
  onClose,
}) => {
  const { showSuccess, showError } = useNotify();
  const [adding, setAdding] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [deletePasskey, { loading: deleting }] = useMutation(DELETE_PASSKEY, {
    refetchQueries: [SECURITY_SETTINGS],
    awaitRefetchQueries: true,
    onCompleted: () => showSuccess('Passkey deleted'),
    onError: (err) => showError(err.message),
  });

  const columns = useMemo(
    () =>
      passkeyColumns((passkey) => {
        const label = passkey.device
          ? `the passkey on ${passkey.device}`
          : 'this passkey';
        if (window.confirm(`Delete ${label}?`)) {
          deletePasskey({ variables: { id: passkey.id } });
        }
      }, deleting),
    [deletePasskey, deleting]
  );

  const added = async () => {
    await onAdded();
    showSuccess('Passkey added');
  };

  const addWithoutPassword = async () => {
    setAdding(true);
    try {
      await registerPasskey();
      await added();
    } catch (err) {
      showError((err as Error).message);
    } finally {
      setAdding(false);
    }
  };

  if (confirming) {
    return (
      <ConfirmPasswordModal
        title="Add passkey"
        confirmLabel="Continue"
        confirmVariant="primary"
        description="Passkeys let you sign in with your device instead of a password."
        onConfirm={async (password) => {
          await registerPasskey(false, password);
          setConfirming(false);
          await added();
        }}
        onClose={() => setConfirming(false)}
      />
    );
  }

  return (
    <Modal
      title="Passkeys"
      onClose={onClose}
      sx={{ width: ['100%', '100%', 520] }}
    >
      <ModalBody>
        <Box m={3}>
          {passkeys.length ? (
            <GridTable columns={columns} items={passkeys} />
          ) : (
            <Text variant="body2" color="fieldBorderHover">
              You have not added a passkey yet.
            </Text>
          )}
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="plus"
          onClick={() =>
            hasPassword ? setConfirming(true) : addWithoutPassword()
          }
          disabled={adding}
        >
          Add passkey
        </Button>
        <Button variant="outline" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PasskeysModal;
